import { HttpClient, HttpHeaders } from "@angular/common/http";
import { map } from 'rxjs/operators';

import {getUrlParams} from "app/lib/lib";
import {Injectable} from "@angular/core";
import { ObservaleQueue2 } from "app/lib/ObservaleQueue2";
import { FilterParams } from "app/models/filter_form.model";
import { Observable } from "rxjs";
import { Pagination } from "../models/pagination.model";
import { StatsRefs } from "../views/referers/ref.model";
import { IBarCharts, ICharts, IDataRequestResult, IStatResult, ITotals } from "../models/responce_result.model";
import { EventsService } from "../lib/EventsService";
import { AppConfig } from 'app/config/config';

type DataProvider2Method = 'get' | 'post' | 'put' | 'delete';

class RoutePathNode {
	constructor(
		public path: string = '',
	) {

	}
	
	toString() {
		return this.path;
	}
}

export class DataRequest {
	constructor(
		public dp2: DataProvider2,
		public nodes: RoutePathNode[] = [],
	) {

	}

	add(node: RoutePathNode) {
		this.nodes.push(node);

		return this;
	}

	getFullRoute(): string {
		return `/${this.nodes.join('/')}`;
	}
	
	getRealFullRoute() {
		return `${this.dp2.getUrl(this.getFullRoute())}`;
	}

	clone() {
		return new DataRequest(this.dp2, [...this.nodes]);
	}

	request<T extends IDataRequestResult>(method: DataProvider2Method, action, fp: FilterParams): Observable<T> {
		const newRoot = this.clone();

		newRoot.add(
			new RoutePathNode(action)
		);
		
		let q$;
		if (method === 'get') {
			q$ = this.dp2.get(newRoot.getFullRoute(), fp);
		} else if (method === 'post') {
			q$ = this.dp2.post(newRoot.getFullRoute(), fp);
		} else if (method === 'put') {
			q$ = this.dp2.put(newRoot.getFullRoute(), fp);
		} else if (method === 'delete') {
			q$ = this.dp2.delete(newRoot.getFullRoute(), fp);
		}
		
		return q$.pipe(map(r => r));

	}

	chart<T extends ICharts>(fp: FilterParams, action: string = 'charts'): Observable<T> {
		return this.request<T>('get', action, fp);
	}

	chartSmallSource<T extends ICharts>(fp: FilterParams, action: string = 'chartSmallSource'): Observable<T> {
		return this.request<T>('get', action, fp);
	}

	total<T extends ITotals>(fp: FilterParams, action: string = 'total'): Observable<T> {
		return this.request<T>('get', action, fp);
	}

	pagination(fp: FilterParams, action: string = 'pagination'): Observable<Pagination> {
		return this.request<Pagination>('get', action, fp);
	}

	stats<T extends IStatResult>(fp: FilterParams, action: string = 'stats'): Observable<T> {
		return this.request<T>('get', action, fp);
	}

	count<T extends IDataRequestResult>(fp: FilterParams): Observable<T> {
		return this.request<T>('get', 'count', fp);
	}

	downloadFileRoute(fp: FilterParams, action: string = 'csv'): string {
		const newRoot = this.clone();

		newRoot.add(
			new RoutePathNode(action)
		);

		return `${this.dp2.getUrl(newRoot.getFullRoute())}?${getUrlParams(fp)}`;
	}

	detail<T extends IStatResult>(fp: FilterParams, action: string = 'detail'): Observable<T> {
		return this.request<T>('get', action, fp);
	}

	refs(fp: FilterParams) {
		return this.request<StatsRefs>('get', 'refs', fp);
	}

	status<T extends IDataRequestResult>(fp: FilterParams): Observable<T> {
		return this.request<T>('get', 'status', fp);
	}

	get<T extends IDataRequestResult>(fp: FilterParams, action: string = ''): Observable<T> {
		return this.request<T>('get', action, fp);
	}

	list<T extends IStatResult>(fp: FilterParams): Observable<T> {
		return this.request<T>('get', 'list', fp);
	}

	report<T extends IDataRequestResult>(fp: FilterParams, action: string = 'stats'): Observable<T> {
		return this.request<T>('get', action, fp);
	}

	barChart<T extends IBarCharts>(fp: FilterParams, action: string = 'charts'): Observable<T> {
		return this.request<T>('get', action, fp);
	}
	
	// ????
	// rest() {
	// 	return this.request<T>('get', action, fp);
	// }
	post<T extends IDataRequestResult>(fp: FilterParams, action: string = ''): Observable<T> {
		return this.request<T>('post', action, fp);
	}

	put<T extends IDataRequestResult>(fp: FilterParams, action: string = ''): Observable<T> {
		return this.request<T>('put', action, fp);
	}

	delete<T extends IDataRequestResult>(fp: FilterParams, action: string = ''): Observable<T> {
		return this.request<T>('delete', action, fp);
	}
}


@Injectable()
export class DataProvider2 {
	public baseRoute: string = AppConfig.backendPrefix;
	
	constructor(
		public http: HttpClient,
		public eventsService: EventsService,
		public q$: ObservaleQueue2,
	) {
		
	}

	getUrl(endpoint: string) {
		return `${this.baseRoute}${endpoint}`;
	}
	
	get<T extends object>(endpoint: string, queryParams?, httpParams?): Observable<T> {
		const url = this.getUrl(endpoint);
		const params = getUrlParams(queryParams);
		
		return this.q$.push(
			this.http.get(`${url}?${params}`, httpParams).pipe(
				map(r => <T>r[1])
			)
		);
	}

	post<T extends object>(endpoint: string, queryParams): Observable<T> {
		return this.q$.push(
			this.http.post(this.getUrl(endpoint), queryParams).pipe(
				map(r => <T>r[1])
			)
		);
	}
	
	put<T extends object>(endpoint: string, queryParams): Observable<T> {
		return this.q$.push(
			this.http.put(this.getUrl(endpoint), queryParams).pipe(
				map(r => <T>r[1])
			)
		);
	}
	
	delete<T extends object>(endpoint: string, queryParams?): Observable<T> {
		const endpount = this.getUrl(endpoint);

		let $r;

		if (queryParams) {
			$r = this.http.request('delete', endpount, {
				headers: new HttpHeaders({
					'Content-Type': 'application/json',
				}),
				body: queryParams,
			});
		} else {
			$r = this.http.delete(endpount);
		}

		return this.q$.push(
			$r.pipe(
				map(r => <T>r[1])
			)
		);
	}
}


@Injectable()
export class DataProviderFactory {
	constructor(
		public http: HttpClient,
		public dp2: DataProvider2,
	) {
		
	}

	getNew(route: string = '') {
		return new DataRequest(
			this.dp2,
			[
				new RoutePathNode(route),
			]
		);
	}
}
